import { MTableToolbar } from "@material-table/core";
import axios from "axios";
import qs from "qs";
import { useRef, useState, createContext, useMemo, useEffect } from "react";
import { Row, Container, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useParams } from "react-router-dom";

import { AUTH_ROLES } from "api/common/types/auth.types";
import { useRemoveFileMutation } from "api/memberPortal/files/files.api";
import { useGetProductQuery } from "api/memberPortal/project/product/product.api";
import RelabelObject from "components/DocumentManager/RelabelObject";
import RenameObject from "components/DocumentManager/RenameObject";
import FileUploadModal from "components/Products/FileUpload";
import DeleteModal from "components/UI/DeleteModal";
import ResponsiveMaterialTable from "components/UI/MaterialTable/ResponsiveMaterialTable";
import UnauthorizedView from "components/UI/UnauthorizedView";
import { useAuth } from "hooks/useAuth";
import { useInterval } from "hooks/useInterval";

import JumpVidFiles from "./ProjectUtils/JumpVidFiles";
import ReplaceFileModal from "./ReplaceFileModal";
import { buildPfaColumns } from "./table/Pfa.columns";

export const PfaContext = createContext();

const FileTableCheckPermissions = () => {
  const { managerArea = "", type } = useParams();
  const { authToken, permissions } = useAuth();

  const validManagerTypes = ["protectionprofiles", "products", "pcm"];

  const { id } = useParams();
  const [hasPerm, setHasPerm] = useState(false);

  useEffect(() => {
    if (type === "product" && id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/has_permissions_to_view_product/?product_id=${id}`,
          {
            withCredentials: true,
            headers: { Authorization: `Token ${authToken}` },
          },
        )
        .then((response) => {
          if (response.data?.authorized === false) {
            setHasPerm(false);
          } else {
            setHasPerm(true);
          }
        })
        .catch(() => {
          setHasPerm(false);
        });
    }
    if (type === "assurance-maintenance" && id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}project/maintenance/has_permissions_to_view_maintenance/?maintenance_id=${id}`,
          {
            withCredentials: true,
            headers: { Authorization: `Token ${authToken}` },
          },
        )
        .then((response) => {
          if (response.data?.authorized === false) {
            setHasPerm(false);
          } else {
            setHasPerm(true);
          }
        })
        .catch(() => {
          setHasPerm(false);
        });
    }
  }, [authToken, type, id, permissions]);

  if (permissions.role_type === "NIAP") return <FileTable />;

  if (
    !validManagerTypes.includes(managerArea) ||
    type === "protection-profile" ||
    type === "ccp" ||
    (managerArea === "products" && !hasPerm)
  ) {
    return <UnauthorizedView />;
  }

  return <FileTable />;
};

const FileTable = () => {
  const { authToken } = useAuth();
  const { id, type } = useParams();
  const tableRef = useRef();

  const [triggerRemoveFile] = useRemoveFileMutation();

  // Modal Controls
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showReplaceFileModal, setShowReplaceFileModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showRelabelModal, setShowRelabelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeFile, setActiveFile] = useState({});

  const { data: product = {} } = useGetProductQuery(
    id,
    { skip: type !== "product" },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const columns = useMemo(() => buildPfaColumns({ product }), [product]);

  const reloadDocuments = () => {
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  };
  useEffect(() => {
    reloadDocuments();
  }, [type, id]);

  const { permissions } = useAuth();

  const canViewJumpVid =
    type === "product" &&
    (permissions.role_type === AUTH_ROLES.NIAP ||
      permissions.role_type === AUTH_ROLES.VALIDATOR ||
      permissions.role_type === AUTH_ROLES.LAB);

  const updateFiles = () => {
    setShowFileUploadModal(false);
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  };

  const handleSoftDeleteFile = () => {
    triggerRemoveFile(activeFile.file_id);
    setShowDeleteModal(false);
    reloadDocuments();
  };

  useInterval(() => {
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  }, 300000); //5 min

  const options = {
    headerStyle: {
      fontWeight: "bold",
      borderBottom: "none",
      paddingBottom: "0",
    },
    filtering: false,
    columnsButton: true,
    pageSize: 20,
    emptyRowsWhenPaging: false,
  };

  const capitalizeHyphens = (text) => {
    return text
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("-");
  };

  const modals = {
    replace: {
      open: () => setShowReplaceFileModal(true),
      close: () => setShowReplaceFileModal(false),
    },
    relabel: {
      open: () => setShowRelabelModal(true),
      close: () => setShowRelabelModal(false),
    },
    rename: {
      open: () => setShowRenameModal(true),
      close: () => setShowRenameModal(false),
    },
    delete: {
      open: () => setShowDeleteModal(true),
      close: () => setShowDeleteModal(false),
      deleteFunction: handleSoftDeleteFile,
    },
  };

  return (
    <PfaContext.Provider
      value={{
        activeFile,
        setActiveFile,
        modals,
        tableRef,
      }}
    >
      <Container className="mt-3">
        <Row className="mb-3">{canViewJumpVid && <JumpVidFiles />}</Row>
        <Row className="justify-content-end mb-3">
          <Col md={2}>
            <Button
              onClick={() => setShowFileUploadModal(true)}
              className="text-capitalize"
            >
              Upload{" "}
              {type === "product" ? "Project" : type === "ccp" ? "PCM" : type}{" "}
              File
            </Button>
          </Col>
        </Row>
        {type !== "" && (
          <ResponsiveMaterialTable
            title={
              type === "product"
                ? `Project File Area for VID ${product?.v_id}`
                : (type === "ccp" ? "PCM" : capitalizeHyphens(type)) +
                  " File Area"
            }
            columns={columns}
            tableRef={tableRef}
            components={{
              Toolbar:
                type === "assurance-maintenance"
                  ? (props) => {
                      return (
                        <MTableToolbar
                          {...props}
                          title={`Project Files for Maintenance Release ${id}`}
                        />
                      );
                    }
                  : undefined,
            }}
            data={(query) =>
              new Promise((resolve, reject) => {
                // Extract the necessary information from the query object
                const {
                  page,
                  pageSize,
                  search,
                  filters,
                  orderBy,
                  orderDirection,
                } = query;

                let newFilters = filters.map((filter) => {
                  let value = "";
                  if (Array.isArray(filter.value)) {
                    value = filter.value;
                  } else {
                    value = filter.value.replace(/['"]+/g, "");
                  }

                  return `"${filter.column.field.replace(/\./g, "__")}":"${value}"`;
                });
                const params = {
                  offset: page * pageSize,
                  limit: pageSize,
                  search: search,
                  filters: newFilters,
                  orderBy: orderBy?.field,
                  orderDirection: orderDirection,
                  file_type: type,
                  file_type_id: id,
                  show_latest_ear_file: type === "product",
                };

                axios
                  .get(
                    `${import.meta.env.VITE_DJANGO_ENDPOINT}file/get_files_by_type_and_type_id?${qs.stringify(
                      params,
                      {
                        arrayFormat: "comma",
                      },
                    )}`,
                    {
                      withCredentials: true,
                      headers: {
                        Authorization: `Token ${authToken}`,
                      },
                    },
                  )
                  .then((response) => {
                    resolve({
                      data: response.data.results,
                      page: page,
                      totalCount: response.data.count,
                    });
                  })
                  .catch((error) => {
                    reject(error);
                  });
              })
            }
            options={options}
          />
        )}

        {/* Modals */}
        {showFileUploadModal && (
          <FileUploadModal
            show={showFileUploadModal}
            updateFiles={updateFiles}
            type={type}
            id={id}
            vid={product.v_id}
          />
        )}

        {showReplaceFileModal && (
          <ReplaceFileModal
            show={true}
            setShow={setShowReplaceFileModal}
            updateFiles={updateFiles}
            file={activeFile}
            type={type}
          />
        )}

        {showRenameModal && (
          <RenameObject
            show={true}
            handleClose={modals.rename.close}
            reloadDocuments={reloadDocuments}
            object={activeFile}
          />
        )}

        {showRelabelModal && (
          <RelabelObject
            show={true}
            handleClose={modals.relabel.close}
            reloadDocuments={reloadDocuments}
            object={activeFile}
          />
        )}

        {showDeleteModal && (
          <DeleteModal
            show={true}
            handleClose={modals.delete.close}
            type="File"
            deleteFunction={modals.delete.deleteFunction}
            isPermanentDelete={true}
          />
        )}
      </Container>
    </PfaContext.Provider>
  );
};

export default FileTableCheckPermissions;
